import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Home,
  LoaderCircle,
  Minus,
  Plus,
  Search,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useSharedQuote } from '../../context/QuoteContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useOrgResidentialRooms } from '../../hooks/useResidentialRooms';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { ReadOnlyBanner } from '../../components/ui/ReadOnlyBanner';
import { CustomDropdown } from '../../components/ui/CustomDropdown';
import { api } from '../../lib/api';
import type {
  OrgResidentialRoom,
  OrgInventoryItem,
  QuoteResidentialRoom,
  QuoteResidentialRoomItem,
  QuoteRoomProfileLevel,
  RoomProfileLevel,
} from '../../types/admin';

// ── Constants ──────────────────────────────────────────────────────────────────

const PROFILE_LEVELS: QuoteRoomProfileLevel[] = ['empty', 'minimal', 'standard', 'full', 'heavy'];

const LEVEL_LABELS: Record<QuoteRoomProfileLevel, string> = {
  empty: 'Empty',
  minimal: 'Minimal',
  standard: 'Standard',
  full: 'Full',
  heavy: 'Heavy',
};

// ── Helpers ────────────────────────────────────────────────────────────────────

let nextId = 1;
function generateId(): string {
  return `qrr-${Date.now()}-${nextId++}`;
}

/**
 * Compare a room's items to each profile to detect if it matches a preset.
 * Returns the matching profile level, or 'custom' if no match.
 */
function detectProfileMatch(
  room: QuoteResidentialRoom,
  orgRoom: OrgResidentialRoom | undefined,
): QuoteRoomProfileLevel | 'custom' {
  // Empty room = 'empty'
  if (room.items.length === 0) return 'empty';
  if (!orgRoom?.profiles) return 'custom';

  for (const profile of orgRoom.profiles) {
    if (profile.items.length !== room.items.length) continue;

    // Check if every item matches by id and quantity (order-independent)
    const profileMap = new Map(profile.items.map(i => [i.itemId, i.quantity]));
    const roomMap = new Map(room.items.map(i => [i.itemId, i.quantity]));

    if (profileMap.size !== roomMap.size) continue;

    let match = true;
    for (const [itemId, qty] of profileMap) {
      if (roomMap.get(itemId) !== qty) { match = false; break; }
    }
    if (match) return profile.level;
  }

  return 'custom';
}

// ── Quantity Controls ──────────────────────────────────────────────────────────

function QtyControls({
  quantity,
  onIncrement,
  onDecrement,
  onDirectEdit,
}: {
  quantity: number;
  onIncrement: () => void;
  onDecrement: () => void;
  onDirectEdit: (n: number) => void;
}) {
  const [raw, setRaw] = useState(String(quantity));
  const [editing, setEditing] = useState(false);

  if (!editing && raw !== String(quantity)) {
    setRaw(String(quantity));
  }

  return (
    <div className="flex items-center gap-1 shrink-0">
      <button
        type="button"
        onClick={onDecrement}
        className="w-8 h-8 flex items-center justify-center rounded-lg border border-stone
                   text-slate hover:text-midnight hover:border-plum/40 active:bg-mist
                   transition-colors duration-100"
        aria-label="Decrease quantity"
      >
        <Minus size={14} />
      </button>
      <input
        type="number"
        min="0"
        step="1"
        value={raw}
        onFocus={() => setEditing(true)}
        onChange={e => setRaw(e.target.value)}
        onBlur={() => {
          const n = parseInt(raw, 10);
          if (isNaN(n) || n < 0) setRaw(String(quantity));
          else onDirectEdit(n);
          setEditing(false);
        }}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className="w-12 h-8 text-center rounded-lg border border-stone bg-white
                   text-small font-semibold text-midnight
                   focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                   transition-colors duration-150"
        aria-label="Quantity"
      />
      <button
        type="button"
        onClick={onIncrement}
        className="w-8 h-8 flex items-center justify-center rounded-lg border border-stone
                   text-slate hover:text-midnight hover:border-plum/40 active:bg-mist
                   transition-colors duration-100"
        aria-label="Increase quantity"
      >
        <Plus size={14} />
      </button>
    </div>
  );
}

// ── Confirm Modal ──────────────────────────────────────────────────────────────

function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onClose,
  dangerConfirm,
}: {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
  dangerConfirm?: boolean;
}) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-2xl max-w-sm w-full mx-4 shadow-brand-lg overflow-hidden">
        <div className="px-5 pt-5 pb-4">
          <h3 className="font-display text-h3 text-midnight mb-2">{title}</h3>
          <p className="text-small text-slate leading-relaxed">{message}</p>
        </div>
        <div className="px-5 pb-5 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className={`flex-1 h-11 rounded-xl text-small font-semibold transition-colors duration-150
              ${dangerConfirm
                ? 'bg-plum text-white hover:bg-plum/90 active:bg-plum/80'
                : 'border border-stone text-slate hover:text-midnight hover:border-midnight/20'
              }`}
          >
            {cancelLabel ?? 'Cancel'}
          </button>
          <button
            type="button"
            onClick={() => { onConfirm(); onClose(); }}
            className={`flex-1 h-11 rounded-xl text-small font-semibold transition-colors duration-150
              ${dangerConfirm
                ? 'border border-rose/30 text-rose hover:bg-rose/10'
                : 'bg-plum text-white hover:bg-plum/90 active:bg-plum/80'
              }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add Room Sheet ────────────────────────────────────────────────────────────

interface AddRoomSheetProps {
  orgRooms: OrgResidentialRoom[];
  onAdd: (room: OrgResidentialRoom, level: QuoteRoomProfileLevel) => void;
  onClose: () => void;
}

function AddRoomSheet({ orgRooms, onAdd, onClose }: AddRoomSheetProps) {
  const [search, setSearch] = useState('');
  const [selectedRoom, setSelectedRoom] = useState<OrgResidentialRoom | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<QuoteRoomProfileLevel>('standard');

  // Respect org-level sort order
  const sortedRooms = [...orgRooms].sort((a, b) => {
    const oa = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const ob = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
    if (oa !== ob) return oa - ob;
    return a.name.localeCompare(b.name);
  });

  const filtered = search.trim()
    ? sortedRooms.filter(r => r.name.toLowerCase().includes(search.trim().toLowerCase()))
    : sortedRooms;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl flex flex-col max-h-[85vh]
                      md:rounded-2xl md:max-w-lg md:w-full md:max-h-[80vh]">

        {/* Header */}
        <div className="px-4 pt-5 pb-3 flex-shrink-0">
          <div className="w-10 h-1 bg-stone rounded-full mx-auto mb-4 md:hidden" />
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-h3 text-midnight">
              {selectedRoom ? 'Choose Profile' : 'Add Room'}
            </h2>
            <button
              type="button"
              onClick={selectedRoom ? () => setSelectedRoom(null) : onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                         hover:text-midnight hover:bg-mist transition-colors"
              aria-label={selectedRoom ? 'Back' : 'Close'}
            >
              {selectedRoom ? <ChevronLeft size={16} /> : <X size={16} />}
            </button>
          </div>

          {!selectedRoom && (
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                <Search size={14} className="text-slate" />
              </div>
              <input
                type="search"
                placeholder="Search rooms…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full h-10 pl-9 pr-3 rounded-lg border border-stone bg-mist
                           text-small text-midnight placeholder:text-slate
                           focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                           transition-colors duration-150"
              />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {!selectedRoom ? (
            /* Room list with Standard quick-add and profile chooser */
            filtered.length === 0 ? (
              <div className="flex flex-col items-center py-12 gap-2 text-center">
                <p className="text-small text-slate">
                  No rooms found{search ? ` for "${search}"` : ''}.
                </p>
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {filtered.map(room => {
                  const hasStandard = room.profiles?.some(p => p.level === 'standard') ?? false;
                  return (
                    <li key={room.id}>
                      <div className="flex items-center gap-2 bg-white border border-stone
                                      rounded-xl px-3 py-3">
                        <div className="shrink-0 w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center">
                          <Home size={16} className="text-plum" />
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <p className="text-small font-medium text-midnight truncate">{room.name}</p>
                          <p className="text-[12px] text-slate">
                            {room.profiles?.length ?? 0} profile{(room.profiles?.length ?? 0) !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {hasStandard && (
                            <button
                              type="button"
                              onClick={() => {
                                onAdd(room, 'standard');
                                onClose();
                              }}
                              className="h-8 px-3 rounded-lg bg-plum text-white
                                         text-[12px] font-semibold
                                         hover:bg-rose active:opacity-80
                                         transition-colors duration-100"
                            >
                              Standard
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setSelectedRoom(room)}
                            className="h-8 w-8 flex items-center justify-center rounded-lg
                                       border border-stone text-slate
                                       hover:border-plum hover:text-plum active:bg-mist
                                       transition-colors duration-100"
                            aria-label={`Choose profile for ${room.name}`}
                          >
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )
          ) : (
            /* Profile level selector */
            <div className="flex flex-col gap-3">
              <p className="text-[13px] text-slate">
                Select a profile level for <span className="font-semibold text-midnight">{selectedRoom.name}</span>.
                Items will be pre-populated and you can adjust them.
              </p>
              <ul className="flex flex-col gap-2">
                {PROFILE_LEVELS.map(level => {
                  const profile = level === 'empty' ? null : selectedRoom.profiles?.find(p => p.level === level);
                  const itemCount = level === 'empty' ? 0 : (profile?.items?.length ?? 0);
                  const isSelected = selectedLevel === level;

                  return (
                    <li key={level}>
                      <button
                        type="button"
                        onClick={() => setSelectedLevel(level)}
                        className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 border
                          transition-all duration-100
                          ${isSelected
                            ? 'border-plum bg-plum/5'
                            : 'border-stone bg-white hover:border-plum/30 active:bg-mist'
                          }`}
                      >
                        <div className="flex-1 min-w-0 text-left">
                          <p className={`text-small font-semibold ${isSelected ? 'text-plum' : 'text-midnight'}`}>
                            {LEVEL_LABELS[level]}
                          </p>
                          <p className="text-[12px] text-slate">
                            {level === 'empty' ? 'No items — add manually' : `${itemCount} item${itemCount !== 1 ? 's' : ''}`}
                            {profile?.averageSquareFeet ? ` · ~${profile.averageSquareFeet} sq ft` : ''}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="shrink-0 w-5 h-5 rounded-full bg-plum flex items-center justify-center">
                            <div className="w-2 h-2 rounded-full bg-white" />
                          </div>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>

              <button
                type="button"
                onClick={() => {
                  onAdd(selectedRoom, selectedLevel);
                  onClose();
                }}
                className="w-full h-12 rounded-xl bg-plum text-white text-[15px] font-semibold
                           hover:bg-rose active:opacity-80 transition-colors duration-150 mt-2"
              >
                Add {selectedRoom.name}
              </button>
            </div>
          )}
        </div>

        {/* Done — only when browsing rooms */}
        {!selectedRoom && (
          <div className="px-4 pt-2 pb-6 flex-shrink-0 border-t border-stone">
            <button
              type="button"
              onClick={onClose}
              className="w-full h-12 rounded-xl bg-plum text-white text-[15px] font-semibold
                         hover:bg-rose active:opacity-80 transition-colors duration-150"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Room Card ──────────────────────────────────────────────────────────────────

interface RoomCardProps {
  room: QuoteResidentialRoom;
  orgRoom: OrgResidentialRoom | undefined;
  orgItemMap: Map<string, OrgInventoryItem>;
  expanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
  onUpdateItem: (itemIdx: number, qty: number) => void;
  onAddItem: () => void;
  onChangeProfile: (level: QuoteRoomProfileLevel) => void;
  disabled: boolean;
}

function RoomCard({
  room,
  orgRoom,
  orgItemMap,
  expanded,
  onToggle,
  onRemove,
  onUpdateItem,
  onAddItem,
  onChangeProfile,
  disabled,
}: RoomCardProps) {
  const totalItems = room.items.reduce((sum, i) => sum + i.quantity, 0);
  const currentMatch = detectProfileMatch(room, orgRoom);
  const [showConfirmProfileChange, setShowConfirmProfileChange] = useState(false);
  const [pendingLevel, setPendingLevel] = useState<QuoteRoomProfileLevel | null>(null);

  // Build dropdown options
  const profileOptions: { value: string; label: string }[] = [];
  if (currentMatch === 'custom') {
    profileOptions.push({ value: 'custom', label: 'Custom' });
  }
  profileOptions.push({ value: 'empty', label: 'Empty' });
  for (const level of ['minimal', 'standard', 'full', 'heavy'] as RoomProfileLevel[]) {
    const profile = orgRoom?.profiles?.find(p => p.level === level);
    if (profile) {
      profileOptions.push({
        value: level,
        label: `${LEVEL_LABELS[level]} (${profile.items.length} items)`,
      });
    }
  }

  function handleProfileSelect(value: string) {
    if (value === 'custom') return; // No-op for current custom state
    const level = value as QuoteRoomProfileLevel;
    if (currentMatch === 'custom') {
      // Changing from custom — need confirmation
      setPendingLevel(level);
      setShowConfirmProfileChange(true);
    } else {
      onChangeProfile(level);
    }
  }

  return (
    <>
      <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
        {/* Room header */}
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-3 flex-1 min-w-0 text-left
                       hover:opacity-80 active:opacity-70 transition-opacity duration-100"
          >
            <div className="shrink-0 w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center">
              <Home size={16} className="text-plum" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-small font-semibold text-midnight truncate">{room.roomName}</p>
              <p className="text-[12px] text-slate">
                {totalItems} item{totalItems !== 1 ? 's' : ''}
              </p>
            </div>
          </button>

          {/* Profile dropdown */}
          {!disabled && (
            <div className="shrink-0 min-w-[120px]" onClick={e => e.stopPropagation()}>
              <CustomDropdown
                value={currentMatch}
                options={profileOptions}
                onChange={handleProfileSelect}
                fullWidth
              />
            </div>
          )}
          {disabled && (
            <span className="shrink-0 text-[12px] font-medium text-slate px-2 py-1 rounded-lg bg-mist">
              {currentMatch === 'custom' ? 'Custom' : LEVEL_LABELS[currentMatch]}
            </span>
          )}

          {!disabled && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); onRemove(); }}
              className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg
                         text-slate hover:text-rose hover:bg-rose/10
                         transition-colors duration-100"
              aria-label={`Remove ${room.roomName}`}
            >
              <Trash2 size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={onToggle}
            className="shrink-0"
          >
            <ChevronRight
              size={16}
              className={`text-slate transition-transform duration-150
                ${expanded ? 'rotate-90' : ''}`}
            />
          </button>
        </div>

        {/* Items list (expanded) */}
        {expanded && (
          <div className="border-t border-stone px-4 py-3">
            {room.items.length === 0 ? (
              <p className="text-[13px] text-slate py-2">No items in this room.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {room.items.map((item, idx) => {
                  const orgItem = orgItemMap.get(item.itemId);
                  return (
                    <li
                      key={`${item.itemId}-${idx}`}
                      className="py-1.5"
                    >
                      <div className="flex items-center gap-3">
                        <p className="flex-1 min-w-0 text-[13px] text-midnight truncate">
                          {item.itemName}
                        </p>
                        {!disabled ? (
                          <QtyControls
                            quantity={item.quantity}
                            onIncrement={() => onUpdateItem(idx, item.quantity + 1)}
                            onDecrement={() => onUpdateItem(idx, item.quantity - 1)}
                            onDirectEdit={n => onUpdateItem(idx, n)}
                          />
                        ) : (
                          <span className="text-small font-semibold text-midnight tabular-nums">
                            ×{item.quantity}
                          </span>
                        )}
                      </div>
                      {(orgItem?.description || orgItem?.notes) && (
                        <div className="mt-0.5 space-y-0.5">
                          {orgItem.description && (
                            <p className="text-[11px] text-slate leading-tight">{orgItem.description}</p>
                          )}
                          {orgItem.notes && (
                            <p className="text-[11px] text-slate/70 leading-tight italic">{orgItem.notes}</p>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {!disabled && (
              <button
                type="button"
                onClick={onAddItem}
                className="mt-2 flex items-center gap-1.5 text-[13px] font-semibold text-plum
                           hover:text-rose transition-colors duration-150"
              >
                <Plus size={14} />
                Add Item
              </button>
            )}
          </div>
        )}
      </div>

      {/* Confirm profile change from custom */}
      <ConfirmModal
        isOpen={showConfirmProfileChange}
        title="Replace Room Inventory?"
        message={`This room has been customized. Switching to "${pendingLevel ? LEVEL_LABELS[pendingLevel] : ''}" will replace all items with the preset inventory for that profile.`}
        confirmLabel="Yes — Replace"
        cancelLabel="Keep Current"
        onConfirm={() => { if (pendingLevel) onChangeProfile(pendingLevel); }}
        onClose={() => { setShowConfirmProfileChange(false); setPendingLevel(null); }}
        dangerConfirm
      />
    </>
  );
}

// ── Add Item Sheet ──────────────────────────────────────────────────────────

interface AddItemSheetProps {
  roomId: string;
  roomName: string;
  orgItems: OrgInventoryItem[];
  existingItemIds: Set<string>;
  onAdd: (item: OrgInventoryItem) => void;
  onClose: () => void;
}

function AddItemSheet({ roomName, orgItems, existingItemIds, onAdd, onClose }: AddItemSheetProps) {
  const [search, setSearch] = useState('');

  // Filter residential items, prioritize room-matching items
  const residentialItems = orgItems.filter(i => (i.quoteType ?? 'commercial') === 'residential');
  const query = search.trim().toLowerCase();
  const filtered = query
    ? residentialItems.filter(i => i.name.toLowerCase().includes(query))
    : residentialItems;

  // Sort: items not already in room first, then alphabetically
  const sorted = [...filtered].sort((a, b) => {
    const aExists = existingItemIds.has(a.id) ? 1 : 0;
    const bExists = existingItemIds.has(b.id) ? 1 : 0;
    if (aExists !== bExists) return aExists - bExists;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl flex flex-col max-h-[85vh]
                      md:rounded-2xl md:max-w-lg md:w-full md:max-h-[80vh]">
        <div className="px-4 pt-5 pb-3 flex-shrink-0">
          <div className="w-10 h-1 bg-stone rounded-full mx-auto mb-4 md:hidden" />
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-h3 text-midnight">Add Item to {roomName}</h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                         hover:text-midnight hover:bg-mist transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
              <Search size={14} className="text-slate" />
            </div>
            <input
              type="search"
              placeholder="Search items…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-stone bg-mist
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {sorted.length === 0 ? (
            <div className="flex flex-col items-center py-12 gap-2 text-center">
              <p className="text-small text-slate">
                No items found{search ? ` for "${search}"` : ''}.
              </p>
            </div>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {sorted.map(item => {
                const alreadyInRoom = existingItemIds.has(item.id);
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => { onAdd(item); onClose(); }}
                      className={`w-full flex items-center gap-3 rounded-xl px-3 py-3 border
                        transition-colors duration-100 text-left
                        ${alreadyInRoom
                          ? 'border-stone bg-mist/50 opacity-60'
                          : 'border-stone bg-white hover:border-plum/30 active:bg-mist'
                        }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-small font-medium text-midnight truncate">{item.name}</p>
                        {item.residentialType && (
                          <p className="text-[11px] text-slate capitalize">
                            {item.residentialType.replace('_', ' ')}
                          </p>
                        )}
                        {(item.description || item.notes) && (
                          <div className="mt-0.5 space-y-0.5">
                            {item.description && (
                              <p className="text-[11px] text-slate leading-tight">{item.description}</p>
                            )}
                            {item.notes && (
                              <p className="text-[11px] text-slate/70 leading-tight italic">{item.notes}</p>
                            )}
                          </div>
                        )}
                      </div>
                      {alreadyInRoom && (
                        <span className="text-[11px] text-slate shrink-0">Already added</span>
                      )}
                      <Plus size={14} className="shrink-0 text-plum" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

interface AutoPopulateResponse {
  success: boolean;
  data: {
    rooms: QuoteResidentialRoom[];
    profileLevel: string;
    propertyDetails: Record<string, unknown>;
  };
}

export function ResidentialInventoryPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? '';
  const { quote, loading, updateQuote } = useSharedQuote();
  const { org } = useOrgSettings(orgId);
  const { rooms: orgRooms, loading: orgRoomsLoading } = useOrgResidentialRooms(orgId);
  const { orgItems } = useSharedQuote();

  const [initialized, setInitialized] = useState(false);
  const [rooms, setRooms] = useState<QuoteResidentialRoom[]>([]);
  const [expandedRoomId, setExpandedRoomId] = useState<string | null>(null);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [addItemRoomId, setAddItemRoomId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Auto-populate state
  const [populating, setPopulating] = useState(false);
  const [populateError, setPopulateError] = useState('');
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);

  // Flat rate gate state — must be declared before any conditional returns
  const [switchingToHourly, setSwitchingToHourly] = useState(false);

  // Initialize from quote
  useEffect(() => {
    if (quote && !initialized) {
      setRooms(quote.residentialRooms ?? []);
      setInitialized(true);
    }
  }, [quote, initialized]);

  // Auto-save with 400ms debounce
  useEffect(() => {
    if (!initialized || quote?.quoteStatus !== 'draft') return;
    const timer = setTimeout(() => {
      setSaving(true);
      setSaveError('');
      updateQuote({ residentialRooms: rooms })
        .then(() => setSaving(false))
        .catch(err => {
          setSaveError(err instanceof Error ? err.message : 'Auto-save failed');
          setSaving(false);
        });
    }, 400);
    return () => clearTimeout(timer);
  }, [rooms, initialized, quote?.quoteStatus, updateQuote]);

  // Build an org items lookup map for enriching room items with residential fields
  const orgItemMap = new Map(orgItems.map(i => [i.id, i]));
  // Build org room lookup
  const orgRoomMap = new Map(orgRooms.map(r => [r.id, r]));

  /** Auto-populate rooms from origin property details */
  const handleAutoPopulate = useCallback(async () => {
    if (!orgId || !quoteId) return;
    setPopulating(true);
    setPopulateError('');
    try {
      const res = await api.post<AutoPopulateResponse>(
        `/quotes/${orgId}/${quoteId}/auto-populate-rooms`,
      );
      const newRooms = res.data?.rooms ?? [];
      if (newRooms.length === 0) {
        setPopulateError('No rooms matched the property details. Try adding rooms manually.');
      } else {
        setRooms(newRooms);
        setExpandedRoomId(null); // Collapse all
      }
    } catch (err) {
      setPopulateError(err instanceof Error ? err.message : 'Failed to auto-populate rooms');
    } finally {
      setPopulating(false);
    }
  }, [orgId, quoteId]);

  /** Add a room from the org template with a specific profile level. */
  const handleAddRoom = useCallback((orgRoom: OrgResidentialRoom, level: QuoteRoomProfileLevel) => {
    const profile = level === 'empty' ? null : orgRoom.profiles?.find(p => p.level === level);
    const items: QuoteResidentialRoomItem[] = (profile?.items ?? []).map(pi => {
      const orgItem = orgItemMap.get(pi.itemId);
      return {
        itemId: pi.itemId,
        itemName: pi.itemName,
        quantity: pi.quantity,
        cubicFeet: orgItem?.cubicFeet,
        weightInPounds: orgItem?.weightInPounds,
        moveUnits: orgItem?.moveUnits,
        bulky: orgItem?.bulky,
        requiredDisassembly: orgItem?.requiredDisassembly,
      };
    });

    const newRoom: QuoteResidentialRoom = {
      id: generateId(),
      roomId: orgRoom.id,
      roomName: orgRoom.name,
      profileLevel: level,
      items,
    };

    setRooms(prev => [...prev, newRoom]);
    setExpandedRoomId(newRoom.id);
  }, [orgItemMap]);

  /** Change a room's profile level, replacing all items. */
  const handleChangeProfile = useCallback((roomId: string, level: QuoteRoomProfileLevel) => {
    setRooms(prev => prev.map(room => {
      if (room.id !== roomId) return room;
      const orgRoom = orgRoomMap.get(room.roomId);
      if (level === 'empty') {
        return { ...room, profileLevel: 'empty', items: [] };
      }
      const profile = orgRoom?.profiles?.find(p => p.level === level);
      const items: QuoteResidentialRoomItem[] = (profile?.items ?? []).map(pi => {
        const orgItem = orgItemMap.get(pi.itemId);
        return {
          itemId: pi.itemId,
          itemName: pi.itemName,
          quantity: pi.quantity,
          cubicFeet: orgItem?.cubicFeet,
          weightInPounds: orgItem?.weightInPounds,
          moveUnits: orgItem?.moveUnits,
          bulky: orgItem?.bulky,
          requiredDisassembly: orgItem?.requiredDisassembly,
        };
      });
      return { ...room, profileLevel: level, items };
    }));
  }, [orgRoomMap, orgItemMap]);

  /** Remove a room by id. */
  function handleRemoveRoom(roomId: string) {
    setRooms(prev => prev.filter(r => r.id !== roomId));
    if (expandedRoomId === roomId) setExpandedRoomId(null);
  }

  /** Update an item's quantity in a specific room, removing if qty <= 0. */
  function handleUpdateItem(roomId: string, itemIdx: number, newQty: number) {
    setRooms(prev =>
      prev.map(room => {
        if (room.id !== roomId) return room;
        if (newQty <= 0) {
          return { ...room, items: room.items.filter((_, i) => i !== itemIdx) };
        }
        return {
          ...room,
          items: room.items.map((item, i) =>
            i === itemIdx ? { ...item, quantity: newQty } : item,
          ),
        };
      }),
    );
  }

  /** Add an item to a specific room. */
  function handleAddItemToRoom(roomId: string, item: OrgInventoryItem) {
    setRooms(prev =>
      prev.map(room => {
        if (room.id !== roomId) return room;
        // If item already exists, increment quantity
        const existingIdx = room.items.findIndex(i => i.itemId === item.id);
        if (existingIdx >= 0) {
          return {
            ...room,
            items: room.items.map((ri, i) =>
              i === existingIdx ? { ...ri, quantity: ri.quantity + 1 } : ri,
            ),
          };
        }
        // Add new item with enriched data
        const newItem: QuoteResidentialRoomItem = {
          itemId: item.id,
          itemName: item.name,
          quantity: 1,
          cubicFeet: item.cubicFeet,
          weightInPounds: item.weightInPounds,
          moveUnits: item.moveUnits,
          bulky: item.bulky,
          requiredDisassembly: item.requiredDisassembly,
        };
        return { ...room, items: [...room.items, newItem] };
      }),
    );
  }

  if (loading || orgRoomsLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <p className="text-small text-rose font-semibold">Quote not found</p>
      </div>
    );
  }

  const isDraft = quote.quoteStatus === 'draft';
  const isResidential = quote.type === 'residential';
  const hasPropertyDetails = !!(quote.addresses?.origin as Record<string, unknown> | undefined)?.residentialProperty;

  // Flat rate gate: if org uses flat rate pricing and this quote hasn't been switched to hourly
  const isFlatRateQuote = isResidential
    && org?.residentialPricingModel === 'flat_rate'
    && !quote.pricingOverride;

  // Summary stats
  const totalRooms = rooms.length;
  const totalItems = rooms.reduce((sum, r) => sum + r.items.reduce((s, i) => s + i.quantity, 0), 0);

  return (
    <div className="px-4 pt-6 pb-10 md:max-w-3xl md:mx-auto">
      {/* Back — hidden on md+ */}
      <button
        type="button"
        onClick={() => navigate(`/moves/${quoteId}`)}
        className="md:hidden flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        {quote.customerCompanyName}
      </button>

      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-h1 text-midnight">Rooms</h1>
        <div className="flex items-center gap-2">
          {saving && <LoaderCircle size={16} className="text-slate animate-spin shrink-0" />}
          {isDraft && (
            <>
              {/* Auto-populate button — shown when there are already rooms */}
              {isResidential && hasPropertyDetails && rooms.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowReplaceConfirm(true)}
                  disabled={populating}
                  className="flex items-center gap-1.5 h-9 px-3 rounded-xl
                             border border-stone bg-white
                             text-[13px] font-semibold text-slate
                             hover:border-plum/40 hover:text-plum
                             active:bg-mist disabled:opacity-50
                             transition-colors duration-150"
                >
                  {populating
                    ? <LoaderCircle size={14} className="animate-spin" />
                    : <Sparkles size={14} />
                  }
                  Smart Fill
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowAddSheet(true)}
                className="flex items-center gap-1.5 h-9 px-3 rounded-xl bg-plum text-white
                           text-[13px] font-semibold
                           hover:bg-rose active:opacity-80 transition-colors duration-150"
              >
                <Plus size={15} />
                Add Room
              </button>
            </>
          )}
        </div>
      </div>

      {!isDraft && <ReadOnlyBanner status={quote.quoteStatus} />}

      {/* Flat rate gate — show message when org uses flat rate pricing */}
      {isFlatRateQuote && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4">
          <p className="text-small font-semibold text-amber-900 mb-1">
            Flat Rate Pricing
          </p>
          <p className="text-[13px] text-amber-800 leading-snug mb-3">
            {org?.name ?? 'Your organization'} uses flat rate pricing based on room count and
            add-on factors. Room-by-room inventory is not needed for flat rate quotes.
          </p>
          {isDraft && (
            <button
              type="button"
              disabled={switchingToHourly}
              onClick={async () => {
                setSwitchingToHourly(true);
                try {
                  await updateQuote({ pricingOverride: 'hourly_rate' });
                } finally {
                  setSwitchingToHourly(false);
                }
              }}
              className="h-9 px-4 rounded-xl border border-amber-300 bg-white text-[13px] font-semibold
                         text-amber-900 hover:border-amber-400 hover:bg-amber-50 active:bg-amber-100
                         disabled:opacity-50 transition-colors duration-150"
            >
              {switchingToHourly ? 'Switching…' : 'Switch to Hourly Rate for this Quote'}
            </button>
          )}
        </div>
      )}

      {saveError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{saveError}</p>
        </div>
      )}
      {populateError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{populateError}</p>
          <button type="button" onClick={() => setPopulateError('')}
            className="shrink-0 text-rose hover:text-rose/80">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Summary bar */}
      {totalRooms > 0 && (
        <div className="flex items-center gap-4 mb-4 px-3 py-2 bg-mist rounded-xl">
          <p className="text-[12px] text-slate">
            <span className="font-semibold text-midnight">{totalRooms}</span> room{totalRooms !== 1 ? 's' : ''}
          </p>
          <p className="text-[12px] text-slate">
            <span className="font-semibold text-midnight">{totalItems}</span> total item{totalItems !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      <div className={!isDraft ? 'opacity-80' : ''}>
        {rooms.length === 0 ? (
          <div className="flex flex-col items-center py-12 gap-5 text-center">
            {/* Gradient CTA — auto-populate from property details */}
            {isDraft && isResidential && hasPropertyDetails && (
              <button
                type="button"
                onClick={handleAutoPopulate}
                disabled={populating}
                className="w-full text-left rounded-xl p-4 shadow-brand-md
                           flex items-center gap-4
                           bg-gradient-to-r from-plum to-rose
                           hover:opacity-90 active:opacity-80
                           disabled:opacity-70
                           transition-all duration-150"
              >
                <div className="shrink-0 w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
                  {populating
                    ? <LoaderCircle size={20} className="text-white animate-spin" />
                    : <Sparkles size={20} className="text-white" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-semibold text-white">
                    Smart Fill from Property Details
                  </p>
                  <p className="text-[13px] text-white/80 leading-snug mt-0.5">
                    Auto-populate rooms and inventory based on the origin property.
                  </p>
                </div>
                <ChevronRight size={20} className="text-white/60 shrink-0" />
              </button>
            )}

            <div className="w-16 h-16 rounded-full bg-mist flex items-center justify-center">
              <Home size={28} className="text-slate" />
            </div>
            <div>
              <p className="text-small font-semibold text-midnight mb-1">No rooms added yet</p>
              <p className="text-[13px] text-slate leading-snug">
                {isDraft && isResidential && hasPropertyDetails
                  ? <>Use <span className="font-semibold text-plum">Smart Fill</span> above or tap <span className="font-semibold text-plum">Add Room</span> to get started.</>
                  : <>Tap <span className="font-semibold text-plum">Add Room</span> to select a room template and pre-populate items.</>
                }
              </p>
            </div>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {rooms.map(room => (
              <li key={room.id}>
                <RoomCard
                  room={room}
                  orgRoom={orgRoomMap.get(room.roomId)}
                  orgItemMap={orgItemMap}
                  expanded={expandedRoomId === room.id}
                  onToggle={() =>
                    setExpandedRoomId(prev => (prev === room.id ? null : room.id))
                  }
                  onRemove={() => handleRemoveRoom(room.id)}
                  onUpdateItem={(idx, qty) => handleUpdateItem(room.id, idx, qty)}
                  onAddItem={() => setAddItemRoomId(room.id)}
                  onChangeProfile={level => handleChangeProfile(room.id, level)}
                  disabled={!isDraft}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {showAddSheet && (
        <AddRoomSheet
          orgRooms={orgRooms}
          onAdd={handleAddRoom}
          onClose={() => setShowAddSheet(false)}
        />
      )}

      {addItemRoomId && (() => {
        const targetRoom = rooms.find(r => r.id === addItemRoomId);
        if (!targetRoom) return null;
        const existingIds = new Set(targetRoom.items.map(i => i.itemId));
        return (
          <AddItemSheet
            roomId={addItemRoomId}
            roomName={targetRoom.roomName}
            orgItems={orgItems}
            existingItemIds={existingIds}
            onAdd={item => handleAddItemToRoom(addItemRoomId, item)}
            onClose={() => setAddItemRoomId(null)}
          />
        );
      })()}

      {/* Replace-all confirmation modal */}
      <ConfirmModal
        isOpen={showReplaceConfirm}
        title="Replace All Rooms?"
        message="This will replace all your current rooms and inventory with rooms generated from the origin property details. Any customizations will be lost."
        confirmLabel="Yes — Replace Current Inventory"
        cancelLabel="Cancel"
        onConfirm={handleAutoPopulate}
        onClose={() => setShowReplaceConfirm(false)}
        dangerConfirm
      />
    </div>
  );
}
